/** Production apex — matches AASA host and Universal Links entitlements. */
export const CANONICAL_SITE = 'https://prii.app';

/**
 * VITE_SITE_URL when it is a real http(s) origin, else prii.app. A placeholder value ("[SENSITIVE]" sat in
 * .env.local and shipped in builds through 36) made every share link unparseable, and iOS's share sheet
 * silently drops a URL it cannot parse - invites went out as text only (2026-09-28).
 */
export function resolveSiteUrl(raw) {
  const value = raw == null ? '' : String(raw).trim();
  try {
    const parsed = new URL(value);
    if (parsed.protocol === 'https:' || parsed.protocol === 'http:') {
      return parsed.origin;
    }
  } catch {
    // not a URL - fall through
  }
  return CANONICAL_SITE;
}

/** Canonical https origin for shareable / OG links (build-time VITE_SITE_URL, else prii.app). */
export function getSiteUrl() {
  return resolveSiteUrl(import.meta.env.VITE_SITE_URL);
}

/**
 * Build an absolute https URL for an in-app route path.
 * Paths should match AASA routes: /events/*, /groups/*, /profile/*, /g/*.
 *
 * @param {string} path - App route, e.g. `/groups/uuid` or `/events/uuid`
 */
export function buildAppUrl(path) {
  if (!path || typeof path !== 'string') {
    throw new Error('buildAppUrl: path is required');
  }
  const normalized = path.startsWith('/') ? path : `/${path}`;
  return `${getSiteUrl()}${normalized}`;
}

/**
 * Normalize any URL or path into a canonical https share link.
 * Rewrites capacitor://, localhost, and relative paths — never emits capacitor://.
 */
export function toShareableUrl(urlOrPath) {
  if (!urlOrPath || typeof urlOrPath !== 'string') {
    throw new Error('toShareableUrl: value is required');
  }

  if (urlOrPath.startsWith('/')) {
    return buildAppUrl(urlOrPath);
  }

  try {
    const parsed = new URL(urlOrPath);
    const path = `${parsed.pathname}${parsed.search}${parsed.hash}`;

    if (
      parsed.protocol === 'capacitor:' ||
      parsed.hostname === 'localhost' ||
      parsed.hostname === '127.0.0.1'
    ) {
      return buildAppUrl(path);
    }

    if (parsed.protocol === 'https:') {
      return parsed.href;
    }

    if (parsed.protocol === 'http:' && import.meta.env.DEV) {
      // Dev http origin — still share canonical https so links work for recipients.
      return buildAppUrl(path);
    }
  } catch {
    return buildAppUrl(urlOrPath);
  }

  return buildAppUrl(urlOrPath);
}
